import { ICustomerFinancialYear, IProject, IPortfolio } from '../types';

export interface IPPAppProjectFinancial {
    ID: number;
    ProjectID?: number;
    FYLabel?: string;
    FYValue?: number;
}

export interface IProjectFinancialsBuckets {
    totalCosts?: number;
    prevYears?: number;
    fyCurrent?: number;
    fyPlus1?: number;
    fyPlus2?: number;
    fyPlus3?: number;
    fyPlus4?: number;
    futureCosts?: number;
}

export type IFinancialsBuckets = IProjectFinancialsBuckets & { benefits?: number; npv?: number };

export const getCurrentFYStartYear = (customerFinancialYear?: ICustomerFinancialYear): number => {
    if (!customerFinancialYear?.fyStartDate) return new Date().getFullYear();
    return new Date(customerFinancialYear.fyStartDate).getFullYear();
};

export const formatFYLabel = (startYear: number): string => {
    const pad = (n: number) => (n % 100).toString().padStart(2, '0');
    return `FY ${pad(startYear)}/${pad(startYear + 1)}`;
};

// "FY 26/27" -> 2026
export const parseFYStartYear = (fyLabel?: string): number | null => {
    if (!fyLabel) return null;
    const match = fyLabel.match(/(\d{2})\s*\/\s*(\d{2})/);
    if (!match) return null;
    return 2000 + parseInt(match[1], 10);
};

export const calculateFinancialsBuckets = (
    financials: IPPAppProjectFinancial[] | undefined,
    currentFYStartYear: number
): IProjectFinancialsBuckets => {
    const buckets: IProjectFinancialsBuckets = {};
    if (!financials || financials.length === 0) return buckets;

    const addToBucket = (key: keyof IProjectFinancialsBuckets, value: number) => {
        buckets[key] = (buckets[key] ?? 0) + value;
    };

    financials.forEach(f => {
        if (f.FYValue === null || f.FYValue === undefined) return;

        const fyStartYear = parseFYStartYear(f.FYLabel);
        if (fyStartYear === null) return;

        addToBucket('totalCosts', f.FYValue);

        const offset = fyStartYear - currentFYStartYear;

        if (offset < 0) addToBucket('prevYears', f.FYValue);
        else if (offset === 0) addToBucket('fyCurrent', f.FYValue);
        else if (offset === 1) addToBucket('fyPlus1', f.FYValue);
        else if (offset === 2) addToBucket('fyPlus2', f.FYValue);
        else if (offset === 3) addToBucket('fyPlus3', f.FYValue);
        else if (offset === 4) addToBucket('fyPlus4', f.FYValue);
        else addToBucket('futureCosts', f.FYValue);
    });

    return buckets;
};

export const calculateFinancialsBucketsForProjects = (
    projects: IProject[] | undefined,
    currentFYStartYear: number
): IFinancialsBuckets => {
    const combined: IFinancialsBuckets = {};
    if (!projects || projects.length === 0) return combined;

    const addToBucket = (key: keyof IFinancialsBuckets, value: number) => {
        combined[key] = (combined[key] ?? 0) + value;
    };

    projects.forEach(p => {
        const projectBuckets = calculateFinancialsBuckets(p.PPAppProjectFinancials, currentFYStartYear);

        (Object.keys(projectBuckets) as (keyof IProjectFinancialsBuckets)[]).forEach(key => {
            const value = projectBuckets[key];
            if (value !== undefined) addToBucket(key, value);
        });

        if (p.TotalBenefits !== null && p.TotalBenefits !== undefined) addToBucket('benefits', p.TotalBenefits);
        if (p.NetPresentValue !== null && p.NetPresentValue !== undefined) addToBucket('npv', p.NetPresentValue);
    });

    return combined;
};

export const mergeFinancialsBuckets = (a: IFinancialsBuckets, b: IFinancialsBuckets): IFinancialsBuckets => {
    const merged: IFinancialsBuckets = { ...a };
    (Object.keys(b) as (keyof IFinancialsBuckets)[]).forEach(key => {
        const value = b[key];
        if (value !== undefined) {
            merged[key] = (merged[key] ?? 0) + value;
        }
    });
    return merged;
};

export const calculateFinancialsBucketsForPortfolio = (
    portfolio: IPortfolio | null | undefined,
    currentFYStartYear: number
): IFinancialsBuckets => {
    if (!portfolio) return {};

    let combined: IFinancialsBuckets = calculateFinancialsBucketsForProjects(portfolio.Projects, currentFYStartYear);

    (portfolio.Programmes ?? []).forEach(pr => {
        const programmeBuckets = calculateFinancialsBucketsForProjects(pr.Projects, currentFYStartYear);
        combined = mergeFinancialsBuckets(combined, programmeBuckets);
    });

    return combined;
};

export const formatCurrencyOrBlank = (value?: number): string => {
    if (value === null || value === undefined) return '';
    return value.toLocaleString('en-GB', { maximumFractionDigits: 0 });
};